import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Grid, LinearProgress, Skeleton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { OrderPageTypes } from '../../api';
import type { AccountData } from '../../api/models/AccountData';
import type { PassDatedSessionSelectionDTO } from '../../api/models/PassDatedSessionSelectionDTO';
import type { PassOrderPageData } from '../../api/models/PassOrderPageData';
import { useAvailableDates } from '../../orderPage/multipleOrderPages/useAvailableDates';
import { ScheduleDayGroup } from '../../managePassesPage/components/ScheduleDayGroup';
import { ScheduleSession } from '../../managePassesPage/hooks/usePassScheduleData';
import { EmptyState } from '../../managePassesPage/components/EmptyState';
import { DATE_FORMAT } from '../../consts';
import { getDateFormat, getTimeFormat } from '../../utils/getDateAndTimeFormat';
import { useLanguage } from '../../utils/GeneralUtils';
import { findOrderPageById } from '../passScheduleSelector/utils';
import { WeekStepper } from './WeekStepper';
import {
  buildDatedSessions,
  datedSelectionKey,
  getStudioPrimary,
  groupSessionsByWeek,
  sessionSelectionKey,
  toSessionSelection,
} from './utils';

type PassDatedSessionPickerState = {
  isLoading: boolean;
  isError: boolean;
  notEnough: boolean;
};

type PassDatedSessionPickerProps = {
  account: AccountData;
  passOrderPage: PassOrderPageData;
  startDate: string;
  endDate: string;
  value: PassDatedSessionSelectionDTO[];
  onChange: (value: PassDatedSessionSelectionDTO[]) => void;
  unavailableSelection?: PassDatedSessionSelectionDTO;
  onStateChange?: (state: PassDatedSessionPickerState) => void;
};

const LEFT_TO_RIGHT_ISOLATE = '⁦';
const POP_DIRECTIONAL_ISOLATE = '⁩';
const LOADING_SKELETON_COUNT = 3;

export const PassDatedSessionPicker = ({
  account,
  passOrderPage,
  startDate,
  endDate,
  value,
  onChange,
  unavailableSelection,
  onStateChange,
}: PassDatedSessionPickerProps) => {
  const { t, i18n } = useTranslation();
  const language = useLanguage();
  const theme = useTheme();
  const primary = getStudioPrimary(theme);
  const isRtl = i18n.dir() === 'rtl';
  const requiredCount = passOrderPage.num_punches ?? 0;
  const [activeWeekIndex, setActiveWeekIndex] = useState(0);

  const eventPageIds = useMemo(
    () =>
      (passOrderPage.redeemable_on || [])
        .map((item) => item.order_page_id)
        .filter((orderPageId) => findOrderPageById(account, orderPageId)?.order_page_type === OrderPageTypes.EVENT),
    [passOrderPage, account],
  );

  const { availableDates, isLoading, isError, mutate } = useAvailableDates(
    account.id,
    eventPageIds,
    1,
    undefined,
    endDate,
  );

  const sessions = useMemo(
    () => buildDatedSessions(availableDates, account, language, startDate, endDate),
    [availableDates, account, language, startDate, endDate],
  );
  const weeks = useMemo(() => groupSessionsByWeek(sessions, startDate, endDate), [sessions, startDate, endDate]);

  const notEnough = !isLoading && !isError && sessions.length < requiredCount;

  useEffect(() => {
    onStateChange?.({ isLoading, isError: !!isError, notEnough });
  }, [isLoading, isError, notEnough]);

  useEffect(() => {
    if (unavailableSelection) {
      void mutate();
    }
  }, [unavailableSelection]);

  useEffect(() => {
    if (isLoading || isError) return;
    const availableKeys = new Set(sessions.map(sessionSelectionKey));
    const stillAvailable = value.filter((selection) => availableKeys.has(datedSelectionKey(selection)));
    if (stillAvailable.length !== value.length) {
      onChange(stillAvailable);
    }
  }, [sessions, isLoading, isError]);

  const selectedKeys = useMemo(() => new Set(value.map(datedSelectionKey)), [value]);
  const selectionFull = value.length >= requiredCount;

  const selectedCountByWeek = weeks.map(
    (week) => value.filter((selection) => selection.date >= week.from && selection.date <= week.to).length,
  );

  const handleToggle = (session: ScheduleSession) => {
    const key = sessionSelectionKey(session);
    if (selectedKeys.has(key)) {
      onChange(value.filter((selection) => datedSelectionKey(selection) !== key));
    } else if (!selectionFull) {
      onChange([...value, toSessionSelection(session)]);
    }
  };

  const activeWeek = weeks[activeWeekIndex] ?? weeks[0];
  const activeWeekDates = useMemo(() => {
    const sessionsByDate = new Map<string, ScheduleSession[]>();
    for (const session of activeWeek?.sessions ?? []) {
      sessionsByDate.set(session.date, [...(sessionsByDate.get(session.date) ?? []), session]);
    }
    return Array.from(sessionsByDate.entries());
  }, [activeWeek]);

  const { longDateFormat } = getDateFormat(account);
  const windowText = t('pass_dated_sessions.validWindow', {
    from: `${LEFT_TO_RIGHT_ISOLATE}${moment(startDate, DATE_FORMAT).format(longDateFormat)}`,
    to: `${moment(endDate, DATE_FORMAT).format(longDateFormat)}${POP_DIRECTIONAL_ISOLATE}`,
  });

  const getHint = (): string => {
    if (notEnough) return t('pass_dated_sessions.notEnoughHint');
    if (selectionFull) return t('pass_dated_sessions.allSelected');
    return t('pass_dated_sessions.pickMore', { count: requiredCount - value.length });
  };

  return (
    <Grid container flexDirection="column" gap={1.5}>
      {unavailableSelection && (
        <Alert severity="error" variant="outlined">
          {t('pass_dated_sessions.sessionFilled', {
            date: moment(unavailableSelection.date, DATE_FORMAT).format(isRtl ? 'dddd, D/M' : 'dddd, MM/DD'),
            time: moment.utc(unavailableSelection.start_time * 1000).format(getTimeFormat(account)),
          })}
        </Alert>
      )}
      {isError && (
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Button color="inherit" size="small" onClick={() => mutate()}>
              {t('pass_dated_sessions.retry')}
            </Button>
          }>
          {t('pass_dated_sessions.loadError')}
        </Alert>
      )}
      {notEnough && (
        <Alert severity="warning" variant="outlined">
          {t('pass_dated_sessions.notEnough', { available: sessions.length, required: requiredCount })}
        </Alert>
      )}
      {!isError && (
        <>
          <Grid container flexDirection="column" gap={0.75}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t('pass_schedule.counter', { selected: value.length, required: requiredCount })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={requiredCount > 0 ? Math.min(100, (value.length / requiredCount) * 100) : 0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha(primary.main, 0.125),
                '& .MuiLinearProgress-bar': { backgroundColor: primary.main },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {windowText}
            </Typography>
            <Typography variant="body2" color={notEnough ? 'warning.main' : 'text.secondary'}>
              {getHint()}
            </Typography>
          </Grid>
          {isLoading ? (
            <Grid container flexDirection="column" gap={1.5}>
              {Array.from({ length: LOADING_SKELETON_COUNT }, (_, index) => (
                <Skeleton key={index} variant="rounded" height={72} />
              ))}
            </Grid>
          ) : (
            <>
              <WeekStepper
                weeks={weeks}
                activeWeekIndex={activeWeek?.index ?? 0}
                onChange={setActiveWeekIndex}
                selectedCountByWeek={selectedCountByWeek}
                language={language}
              />
              {activeWeekDates.length > 0 ? (
                activeWeekDates.map(([date, daySessions]) => (
                  <ScheduleDayGroup
                    key={date}
                    date={date}
                    sessions={daySessions}
                    account={account}
                    selectable
                    selectedKeys={selectedKeys}
                    selectionFull={selectionFull}
                    onToggle={handleToggle}
                  />
                ))
              ) : (
                <EmptyState message={t('manage_passes.schedule.noSessions')} />
              )}
            </>
          )}
        </>
      )}
    </Grid>
  );
};
